import * as React from "react";
import {inject, observer} from "mobx-react";
import {observable} from "mobx";
import type {App} from "../../../../../App";
import {TableViewV5} from "../../../abstract/TableViewV5";
import {XyiconFeature} from "../../../../../generated/api/base";
import type {User} from "../../../../../data/models/User";
import type {IActionBarItem} from "../../../abstract/ModuleViewV5";
import DeleteIcon from "../../../icons/delete.svg?react";
import type {AppState} from "../../../../../data/state/AppState";
import {Functions} from "../../../../../utils/function/Functions";
import {UserDetailsContentV5} from "./UserDetailsContentV5";
import {CreateUserPanelV5} from "./CreateUserPanelV5";

interface IUsersProps {
	readonly app?: App;
	readonly appState?: AppState;
}

@inject("app")
@inject("appState")
@observer
export class UsersViewV5 extends React.Component<IUsersProps> {
	@observable
	private _appState: AppState;

	constructor(props: IUsersProps) {
		super(props);
		this._appState = props.appState;
	}

	private get _headers() {
		const headers = [
			{
				id: "fullName",
				title: "Name",
			},
			{
				id: "email",
				title: "Email",
			},
			{
				id: "status",
				title: "Status",
			},
		];

		if (this._appState.organization.isSSOEnabledForOrganization) {
			headers.push({
				id: "isSSOEnabledForUser",
				title: "SSO",
			});
		}

		headers.push({
			id: "lastModifiedAt",
			title: "Last Updated",
		});

		return headers;
	}

	private readonly _tableView = React.createRef<TableViewV5<User>>();

	public override componentDidUpdate(prevProps: IUsersProps) {
		if (prevProps.appState !== this.props.appState) {
			this._appState = this.props.appState;
		}
	}

	public override componentDidMount() {
		this.props.app.moduleViews[XyiconFeature.User] = this._tableView.current;
	}

	private canDeleteUser = (users: User[]) => {
		if (users.length < 1) {
			return false;
		}

		const ownAccountSelected = users.includes(this._appState.user);

		if (ownAccountSelected) {
			return false;
		}

		return true;
	};

	private getActionBar = () => {
		const result: IActionBarItem<User>[] = [
			{
				id: "delete",
				title: "Delete Users",
				label: "Delete",
				IconComponent: DeleteIcon,
				isVisible: Functions.returnTrue,
				isEnabled: this.canDeleteUser,
			},
		];

		return result;
	};

	public override render() {
		return (
			<TableViewV5
				ref={this._tableView}
				feature={XyiconFeature.User}
				headers={this._headers}
				create={(onCreated, onCancel, creating) => (
					<CreateUserPanelV5
						onCancel={onCancel}
						onCreated={onCreated}
						creating={creating}
					/>
				)}
				details={(focused: User[]) => <UserDetailsContentV5 users={focused} />}
				actionBar={this.getActionBar()}
				canDeleteUser={this.canDeleteUser}
			/>
		);
	}
}
